import { Controller } from "stimulus";

const DEFAULT_ERROR_MESSAGE = "Sorry, we couldn't cancel your subscription";

export default class extends Controller {
  static targets = [
    "step",
    "form",
    "otherInput",
    "switchedProductInput",
    "missingFeaturesInput",
    "errorContainer",
    "errorMessage",
    "nextButton"
  ];

  static values = { currentStep: Number };

  connect() {
    this.showCurrentStep();
    this.cancellationSuccessful = false;
    this.surveyData = {};

    if (this.hasFormTarget) this.formTarget.addEventListener('change', this.updateButtonState.bind(this));
    this.updateButtonState();
  }

  nextStep() {
    this.clearErrorMessage();

    if (this.currentStepValue === 1 && !this.isStep1Valid()) return;
    if (this.currentStepValue === 1) this.captureSurveyData();

    if (this.currentStepValue < this.totalSteps()) {
      this.currentStepValue += 1;
      this.showCurrentStep();
    }
  }

  prevStep() {
    if (this.currentStepValue > 1) {
      this.currentStepValue -= 1;
      this.showCurrentStep();
    }
  }

  showCurrentStep() {
    this.stepTargets.forEach((step) => {
      const stepNumber = parseInt(step.dataset.step);

      stepNumber === this.currentStepValue
        ? step.classList.remove("hidden")
        : step.classList.add("hidden");
    });

    this.updateButtonState();
  }

  isStep1Valid() {
    const form = this.formTarget;
    const checkedRadio = form.querySelector('input[type="radio"]:checked');

    return checkedRadio !== null;
  }

  totalSteps() {
    return this.stepTargets.length;
  }

  toggleFollowUpInput(event) {
    // Hide all follow-up inputs first
    if (this.hasOtherInputTarget) this.otherInputTarget.classList.add("hidden");
    if (this.hasSwitchedProductInputTarget) this.switchedProductInputTarget.classList.add("hidden");
    if (this.hasMissingFeaturesInputTarget) this.missingFeaturesInputTarget.classList.add("hidden");

    // Show the appropriate follow-up input based on the selected reason
    if (event.target.checked) {
      const selectedReason = event.target.value;

      switch (selectedReason) {
        case "other":
          if (this.hasOtherInputTarget) this.otherInputTarget.classList.remove("hidden");
          break;
        case "switched_product":
          if (this.hasSwitchedProductInputTarget) this.switchedProductInputTarget.classList.remove("hidden");
          break;
        case "missing_features":
          if (this.hasMissingFeaturesInputTarget) this.missingFeaturesInputTarget.classList.remove("hidden");
          break;
      }
    }
  }

  cancelSubscription() {
    // Submit cancellation with survey data
    this.submitCancellationWithSurveyData();
  }

  onCancellationSuccess(message) {
    // Flag that cancellation was successful so we can refresh on close
    this.cancellationSuccessful = true;

    // Close the modal and refresh to show updated state with flash message
    this.closeModal();
  }

  onCancellationError(event) {
    console.error('Cancellation failed:', event.detail);
    this.showErrorMessage(this.extractErrorMessage(event));
  }

  // Extract error message from event
  extractErrorMessage(event) {
    return event.detail?.[0]?.message || DEFAULT_ERROR_MESSAGE;
  }

  // Display error message to user
  showErrorMessage(message) {
    if (this.hasErrorContainerTarget && this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message;
      this.errorContainerTarget.classList.remove('hidden');
    }
  }



  // If cancellation was successful, refresh the page to show the updated state
  closeModal() {
    // Close the modal using the modal controller
    const modalController = this.application.getControllerForElementAndIdentifier(
      this.element.closest('[data-controller~="modal"]'),
      'modal'
    );
    if (modalController) {
      modalController.close();
    }

    if (this.cancellationSuccessful) window.location.reload();
  }

  // Hide any error messages
  clearErrorMessage() {
    if (this.hasErrorContainerTarget) this.errorContainerTarget.classList.add('hidden');
  }

  // Capture survey data from the form when moving to step 2
  captureSurveyData() {
    if (!this.hasFormTarget) return;

    const formData = new FormData(this.formTarget);
    this.surveyData = {
      reason: formData.get('reason'),
      other_reason: formData.get('other_reason'),
      switched_product_name: formData.get('switched_product_name'),
      missing_features_details: formData.get('missing_features_details')
    };

    // Remove empty values
    Object.keys(this.surveyData).forEach(key => {
      if (!this.surveyData[key]) {
        delete this.surveyData[key];
      }
    });
  }

  // Submit cancellation with survey data
  async submitCancellationWithSurveyData() {
    const cancelButton = this.element.querySelector('[data-action*="cancelSubscription"]');

    this.setButtonState(cancelButton, { disabled: true, text: 'Cancelling...' });

    try {
      const response = await fetch('/subscription', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(this.surveyData)
      });

      if (response.ok) {
        const result = await response.json();
        this.onCancellationSuccess(result.message);
      } else {
        const error = await response.json().catch(() => ({ message: 'Request failed' }));
        this.onCancellationError({ detail: [{ message: error.message }] });
      }
    } catch (error) {
      console.error('Cancellation request failed:', error);
      this.onCancellationError({ detail: [{ message: 'Network error occurred' }] });
    } finally {
      // Reset button state (only on error - success navigates away)
      if (cancelButton?.disabled) {
        this.setButtonState(cancelButton, { disabled: false, text: 'Cancel subscription' });
      }
    }
  }

  // Helper method to manage button state
  setButtonState(button, { disabled, text }) {
    if (!button) return;
    if (disabled !== undefined) button.disabled = disabled;
    if (text !== undefined) button.textContent = text;
  }

  updateButtonState() {
    if (!this.hasNextButtonTarget) return;

    // Only apply validation to step 1 (the form selection step)
    const isEnabled = this.currentStepValue !== 1 || this.isStep1Valid();

    this.nextButtonTarget.disabled = !isEnabled;
    this.nextButtonTarget.classList.toggle('opacity-50', !isEnabled);
    this.nextButtonTarget.classList.toggle('cursor-not-allowed', !isEnabled);
    this.nextButtonTarget.classList.toggle('cursor-pointer', isEnabled);
  }
}
